<script lang="ts">
  /**
   * 검색 페이지 컴포넌트
   *
   * 기능:
   * - 통합 검색: 게시글, 사용자 검색 (전체/분류 검색 지원)
   * - URL 파라미터를 통한 초기 쿼리 지원 (?q=keyword)
   * - 탭 기반 필터링 (전체, 게시글, 사용자)
   * - 검색 결과 로딩 상태 표시
   *
   * Svelte 버전: 4.2.20 (with Svelte 5 호환성)
   * @inertiajs/svelte: 2.3.6 (Svelte 4 & 5 모두 지원)
   *
   * 주의사항:
   * - Svelte 4에서는 JSX 타입 어설션(`as`) 미지원
   * - handleTabChange()에서 런타임 타입 검증으로 우회
   */

  import SearchBar from '../components/SearchBar.svelte';
  import ReputationBadge from '../components/ReputationBadge.svelte';

  interface Post {
    id: string;
    title: string;
    excerpt: string;
    board: string;
    author: string;
    likes_count: number;
    comments_count: number;
    created_at: string;
  }

  interface User {
    id: string;
    name: string;
    nickname: string;
    avatar_url: string;
    reputation_score: number;
    level: number;
    level_name: string;
  }

  interface SearchResults {
    posts: Post[];
    users: User[];
    total: number;
  }

  let query = $state('');
  let activeTab = $state<'all' | 'posts' | 'users'>('all');
  let results = $state<SearchResults>({ posts: [], users: [], total: 0 });
  let isLoading = $state(false);
  let hasSearched = $state(false);

  // URL에서 초기 쿼리 읽기
  $effect(() => {
    const urlQuery = new URLSearchParams(window.location.search).get('q');
    if (urlQuery) {
      query = urlQuery;
      performSearch();
    }
  });

  async function performSearch() {
    if (!query.trim()) return;

    isLoading = true;
    hasSearched = true;

    try {
      const response = await fetch(
        `/api/search?q=${encodeURIComponent(query)}&type=${activeTab}&limit=30`
      );
      const data = await response.json();
      results = data;
    } catch (_error) {
      // Silently handle search errors
    } finally {
      isLoading = false;
    }
  }

  /**
   * 탭 변경 핸들러
   * @param {string} tab - 변경할 탭의 키 ('all', 'posts', 'users')
   *
   * Svelte 5 호환성 수정:
   * - Svelte 4에서는 JSX의 타입 어설션(`as` 키워드)을 지원하지 않음
   * - 따라서 런타임에 타입을 검증하는 방식으로 변경
   * - 이를 통해 Svelte 4.x + @inertiajs/svelte 2.3.6 환경에서 정상 작동
   */
  function handleTabChange(tab: string) {
    // 유효한 탭 값인지 런타임 검증 (Svelte 4 호환성)
    if (tab === 'all' || tab === 'posts' || tab === 'users') {
      activeTab = tab;
      // 이미 검색을 수행했다면 새 탭 선택 시 검색 결과 갱신
      if (hasSearched) {
        performSearch();
      }
    }
  }

  function formatDate(dateStr: string) {
    return new Date(dateStr).toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }

  function handleNavigate(path: string) {
    window.location.href = path;
  }
</script>

<div class="space-y-6">
<div class="py-8 max-w-4xl mx-auto">
    <!-- 헤더 -->
    <div class="mb-8">
      <h1 class="text-h1 text-content mb-4">검색</h1>

      <!-- 검색바 -->
      <SearchBar
        placeholder="게시글, 사용자 검색..."
        autoFocus={true}
        onNavigate={handleNavigate}
      />
    </div>

    <!-- 탭: 검색 필터 선택 -->
    <!--
      Svelte 4 호환성 주의:
      - onclick 핸들러에서 handleTabChange()로 문자열 전달
      - 함수 내부에서 런타임 타입 검증 수행 (Svelte 4는 JSX `as` 미지원)
      - 이렇게 함으로써 타입 안정성을 유지하면서도 Svelte 4 호환 보장
    -->
    <div class="flex gap-2 mb-6 border-b border-default">
      {#each [
        { key: 'all', label: '전체' },
        { key: 'posts', label: '게시글' },
        { key: 'users', label: '사용자' }
      ] as tab}
        <button
          type="button"
          class="px-4 py-2 text-label border-b-2 -mb-px transition-colors
                 {activeTab === tab.key
                   ? 'text-primary border-primary'
                   : 'text-content-tertiary border-transparent hover:text-gray-700 dark:hover:text-dark-text-high'}"
          onclick={() => handleTabChange(tab.key)}
        >
          {tab.label}
        </button>
      {/each}
    </div>

    <!-- 결과 -->
    {#if isLoading}
      <div class="text-center py-12">
        <svg class="animate-spin w-8 h-8 mx-auto text-primary" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
        </svg>
        <p class="mt-2 text-body2 text-content-tertiary">검색 중...</p>
      </div>
    {:else if hasSearched}
      <div class="space-y-6">
        <!-- 결과 수 -->
        <p class="text-caption text-content-tertiary">
          "{query}"에 대한 검색 결과 {results.total}건
        </p>

        <!-- 게시글 결과 -->
        {#if (activeTab === 'all' || activeTab === 'posts') && results.posts?.length > 0}
          <section>
            {#if activeTab === 'all'}
              <h2 class="text-h2 text-content mb-3">게시글</h2>
            {/if}
            
            <div class="space-y-3">
              {#each results.posts as post (post.id)}
                <a
                  href="/posts/{post.id}"
                  class="block card p-4 hover:border-primary/30 hover:shadow-sm transition-all"
                >
                  <h3 class="text-body1 font-medium text-content mb-1">{post.title}</h3>
                  {#if post.excerpt}
                    <p class="text-body2 text-content-secondary mb-2 line-clamp-2">{post.excerpt}</p>
                  {/if}
                  <div class="flex items-center gap-4 text-caption text-content-tertiary">
                    <span>{post.board}</span>
                    <span>{post.author}</span>
                    <span class="flex items-center gap-1"><svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"/></svg>{post.likes_count}</span>
                    <span class="flex items-center gap-1"><svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"/></svg>{post.comments_count}</span>
                    <span>{formatDate(post.created_at)}</span>
                  </div>
                </a>
              {/each}
            </div>
          </section>
        {/if}

        <!-- 사용자 결과 -->
        {#if (activeTab === 'all' || activeTab === 'users') && results.users?.length > 0}
          <section>
            {#if activeTab === 'all'}
              <h2 class="text-h2 text-content mb-3">사용자</h2>
            {/if}
            
            <div class="grid gap-3 sm:grid-cols-2">
              {#each results.users as user (user.id)}
                <a
                  href="/users/{user.id}"
                  class="flex items-center gap-3 card p-4 hover:border-primary/30 hover:shadow-sm transition-all"
                >
                  <img
                    src={user.avatar_url || '/default-avatar.png'}
                    alt=""
                    class="w-12 h-12 rounded-full object-cover"
                  />
                  <div class="flex-1 min-w-0">
                    <p class="text-body2 font-medium text-content truncate">
                      {user.nickname || user.name}
                    </p>
                    <ReputationBadge 
                      level={user.level}
                      levelName={user.level_name}
                      reputationScore={user.reputation_score}
                      size="sm"
                    />
                  </div>
                </a>
              {/each}
            </div>
          </section>
        {/if}

        <!-- 결과 없음 -->
        {#if results.total === 0}
          <div class="text-center py-12">
            <svg class="w-16 h-16 mx-auto text-gray-300 dark:text-dark-text-disabled mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
            </svg>
            <p class="text-body2 text-content-tertiary">검색 결과가 없습니다</p>
            <p class="text-caption text-gray-400 dark:text-dark-text-disabled mt-1">다른 검색어로 시도해보세요</p>
          </div>
        {/if}
      </div>
    {:else}
      <!-- 검색 전 상태 -->
      <div class="text-center py-12">
        <svg class="w-16 h-16 mx-auto text-gray-300 dark:text-dark-text-disabled mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
        </svg>
        <p class="text-body2 text-content-tertiary">검색어를 입력하세요</p>
      </div>
    {/if}
  </div>
</div>
